import { toggleButtonClasses, toggleButtonGroupClasses } from '@mui/material';
import type { ButtonProps } from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import type { ToggleButtonProps } from '@mui/material/ToggleButton';
import Button from './button'; // Import the Button component overrides

declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsColorOverrides {
    default: true;
  }
  interface ToggleButtonPropsSizeOverrides {
    xsmall?: true;
    xlarge?: true;
  }
  interface ToggleButtonOwnProps {
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  }
}

export default function ToggleButton(theme: Theme) {
  // Get the Button component overrides
  const buttonOverrides = Button(theme);
  const buttonRootOverrides = buttonOverrides.MuiButton.styleOverrides?.root;

  const rootStyles = (ownerState: ToggleButtonProps) => {
    // Create a mock ButtonProps with outlined variant to get Button styles
    const mockButtonOwnerState: ButtonProps = {
      variant: 'outlined',
      // Map ToggleButton sizes to Button sizes and handle color mapping
      size: (ownerState.size as ButtonProps['size']) || 'medium',
      // Map ToggleButton colors to Button colors
      color:
        ownerState.color === 'standard'
          ? 'default'
          : (ownerState.color as ButtonProps['color']) || 'default',
    };

    // Get the Button's outlined styles
    let buttonStyles = {};
    if (typeof buttonRootOverrides === 'function') {
      buttonStyles = buttonRootOverrides({ ownerState: mockButtonOwnerState });
    } else if (buttonRootOverrides) {
      buttonStyles = buttonRootOverrides;
    }

    const borderRadius = theme.spacing(0.5);
    const color =
      ownerState.color === 'standard'
        ? 'default'
        : ownerState.color || 'default';

    // ToggleButton specific styles and overrides
    const toggleButtonSpecificStyles = {
      // Remove button-specific properties that don't apply to toggle buttons
      minWidth: 'auto',
      border: 'none',
      color: theme.palette.grey[900],
      backgroundColor: 'transparent',
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',

      [`> svg`]: {
        color:
          color === 'default'
            ? theme.palette.grey[700]
            : theme.palette[color][500],
      },

      [`&:not(.${toggleButtonClasses.disabled}) > svg`]: {
        color: theme.palette.grey[900],
      },
      '&:hover': {
        backgroundColor: theme.palette.common.white,
        borderRadius: borderRadius,
      },
      '&:focus': {
        boxShadow: 'none',

        color:
          color === 'default'
            ? theme.palette.grey[700]
            : theme.palette[color][400],
        [`& > svg`]: {
          color:
            color === 'default'
              ? `${theme.palette.grey[700]} !important`
              : `${theme.palette[color][400]} !important`,
        },
      },

      // Group specific styles for ToggleButtonGroup
      [`&.${toggleButtonGroupClasses.firstButton}`]: {
        border: 'none',
        // borderRadius: borderRadius,
        borderTopRigthRadius: `${borderRadius} !important`,
        borderTopLeftRadius: `${borderRadius} !important`,
        borderBottomRigthRadius: `${borderRadius} !important`,
        borderBottomLeftRadius: `${borderRadius} !important`,
        marginLeft: 0,
      },
      [`&.${toggleButtonGroupClasses.lastButton}`]: {
        border: 'none',
        borderTopRigthRadius: `${borderRadius} !important`,
        borderTopLeftRadius: `${borderRadius} !important`,
        borderBottomRigthRadius: `${borderRadius} !important`,
        borderBottomLeftRadius: `${borderRadius} !important`,
        marginLeft: 0,
      },
      [`&.${toggleButtonGroupClasses.middleButton}`]: {
        border: 'none',
        // borderRadius: borderRadius,
        borderBottomRightRadius: `${borderRadius} !important`,
        borderTopRightRadius: `${borderRadius} !important`,
        borderBottomLeftRadius: `${borderRadius} !important`,
        borderTopLeftRadius: `${borderRadius} !important`,
        marginLeft: 0,
      },

      [`&.${toggleButtonClasses.disabled}`]: {
        // backgroundColor: theme.palette.grey[100],
        color: `${theme.palette.grey[400]} !important`,
        boxShadow: `none!important`,
        borderRadius: borderRadius,
        [`& > svg`]: {
          color: theme.palette.grey[400],
        },
      },

      [`&.${toggleButtonClasses.disabled}.${toggleButtonClasses.selected}`]: {
        // backgroundColor: theme.palette.grey[100],
        color: `${theme.palette.grey[400]} !important`,
        boxShadow: `0 0 0 1px ${theme.palette.grey[400]}!important`,
        borderRadius: borderRadius,

        [`> svg`]: {
          color: `${theme.palette.grey[400]} !important`,
        },
      },

      // Selected state styles (when toggle button is active)
      [`&.${toggleButtonClasses.selected}`]: {
        // Use a slightly different background for selected state
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 0 0 1px ${theme.palette[color || 'primary'][500]}`,
        color: theme.palette[color || 'primary'][500],
        borderRadius: borderRadius,

        [`> svg`]: {
          color: theme.palette[color || 'primary'][500],
        },

        '&:hover': {
          backgroundColor: theme.palette.common.white,
          color:
            color === 'default'
              ? theme.palette.grey[700]
              : theme.palette[color || 'primary'][600],
          boxShadow: `0 0 0 1px ${theme.palette[color || 'primary'][600]}`,
          [`> svg`]: {
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color || 'primary'][600],
          },
        },
        '&:focus': {
          backgroundColor: theme.palette.common.white,
          color: theme.palette[color || 'primary'][500],
          boxShadow: `0 0 0 1px ${theme.palette[color || 'primary'][500]}`,
          // [`> svg`]: {
          //   color:
          //     color === 'default'
          //       ? theme.palette.grey[700]
          //       : theme.palette[color || 'primary'][400],
          // },
        },
      },

      // '&:hover': {
      //   backgroundColor:
      //     color === 'default'
      //       ? theme.palette.grey[200]
      //       : theme.palette[color || 'primary'][100],
      //   borderColor:
      //     color === 'default'
      //       ? theme.palette.grey[500]
      //       : theme.palette[color || 'primary'][600],
      // },

      // Handle custom sizes that don't exist in Button

      ...(ownerState.size === 'xsmall' && {
        height: 24,
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(0.5),
        borderRadius: borderRadius,
      }),
    };

    // Merge Button styles with ToggleButton specific styles
    return {
      ...buttonStyles,
      ...toggleButtonSpecificStyles,
    };
  };

  return {
    MuiToggleButton: {
      defaultProps: {
        color: 'default',
        disableRipple: true,
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ToggleButtonProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
